import * as React from 'react';

import {
  enumDocumentType,
  enumLoadStatus,
  LoadStatus,
} from '@rematter/frontend-lib-api-client';
import { useToast } from '@rematter/ui-web/hooks';
import { useSetAtom } from 'jotai';
import { FileCode2, Printer, Trash2, Undo2 } from 'lucide-react';

import {
  RevertVisibility,
  useRevertLoadStatus,
} from '../_components/loads/hooks/useRevertLoadStatus';
import { LoadAssignBookingPopover } from '../_components/loads/LoadAssignBookingPopover';
import { LoadAssignSalesOrderPopover } from '../_components/loads/LoadAssignSalesOrderPopover';
import type { LoadRecord } from '../types/load';

import { usePrintWithBrowser } from './usePrintWithBrowser';

import { $revertStatus } from '~/modules/trade/_state/loadDetail.state';
import { useTranslations } from '~/modules/trade/i18n/useTranslations';

const isFinished = (status: LoadStatus): status is 'Reconciled' | 'Voided' =>
  status === enumLoadStatus.Reconciled || status === enumLoadStatus.Voided;

export type MenuItem = {
  label: string | React.ReactNode;
  onAction: () => void;
  disabled?: boolean;
};

export const useLoadDetailMenuItems = (
  load: Pick<
    LoadRecord,
    | 'id'
    | 'salesOrderId'
    | 'bookingId'
    | 'status'
    | 'invoiceId'
    | 'heavyWeight'
    | 'lightWeight'
  >,
  revertMenuVisibility: RevertVisibility = 'always',
  onVoid?: (loadId: string) => void,
  onRefetch?: () => void,
) => {
  const toast = useToast();
  const t = useTranslations();
  const [isSalesOrderPopoverOpen, setIsSalesOrderPopoverOpen] =
    React.useState(false);
  const [isBookingPopoverOpen, setIsBookingPopoverOpen] = React.useState(false);

  const revertLoadStatus = useSetAtom($revertStatus);
  const { print, isPrinting } = usePrintWithBrowser();

  const handlePrintInvoice = React.useCallback(() => {
    print({
      documents: [
        {
          loadId: load.id,
          invoiceId: load.invoiceId ?? undefined,
          documentType: enumDocumentType.Invoice,
        },
      ],
    });

    toast.info(t.loadDetail.actions.printingInvoice);
  }, [print, load.id, load.invoiceId, t, toast]);

  const handlePrintPackingList = React.useCallback(() => {
    print({
      documents: [
        {
          loadId: load.id,
          documentType: enumDocumentType.PackingList,
        },
      ],
    });

    toast.info(t.loadDetail.actions.printingPackingList);
  }, [print, load.id, t, toast]);

  const handlePrintBillOfLading = React.useCallback(() => {
    print({
      documents: [
        {
          loadId: load.id,
          documentType: enumDocumentType.BillOfLading,
        },
      ],
    })
      .then(() => toast.success(t.loadDetail.actions.printBillOfLadingSuccess))
      .catch((_error) =>
        toast.error(t.loadDetail.actions.printBillOfLadingError),
      );

    toast.info(t.loadDetail.actions.printingBillOfLading);
  }, [print, load.id, t, toast]);

  const [revertStatus, revertStatusLabel] = useRevertLoadStatus({
    status: load.status,
    salesOrderId: load.salesOrderId,
    visibility: revertMenuVisibility,
  });

  const handleReassignSO = React.useCallback(() => {
    setIsSalesOrderPopoverOpen(true);
  }, []);

  const handleReassignBooking = React.useCallback(() => {
    setIsBookingPopoverOpen(true);
  }, []);

  const handleRevertStatus = React.useCallback(async () => {
    if (!revertStatus) return;
    try {
      await revertLoadStatus({
        id: load.id,
        targetStatus: revertStatus as LoadStatus,
      });

      toast.success(t.loadDetail.actions.revertStatusSuccess);
    } catch (error) {
      console.error('Failed to revert load status:', error);
      toast.error(t.loadDetail.actions.revertStatusError);
    } finally {
      onRefetch?.();
    }
  }, [load.id, revertStatus, revertLoadStatus, t, toast, onRefetch]);

  const handleVoidLoad = React.useCallback(() => {
    if (!load.id) {
      toast.error(t.loadDetail.messages.noLoadIdToVoid);
      return;
    }
    onVoid?.(load.id);
  }, [load.id, t, toast, onVoid]);

  const menuItems = React.useMemo(
    () => [
      {
        label: (
          <div className="flex items-center gap-2">
            <Printer className="size-4" />
            {t.loadDetail.actions.printPackingList}
          </div>
        ),
        onAction: handlePrintPackingList,
        disabled: isPrinting,
      },
      {
        label: (
          <div className="flex items-center gap-2">
            <Printer className="size-4" />
            {t.loadDetail.actions.printBillOfLading}
          </div>
        ),
        onAction: handlePrintBillOfLading,
        disabled: isPrinting,
      },
      ...(load.invoiceId
        ? [
            {
              label: (
                <div className="flex items-center gap-2">
                  <Printer className="size-4" />
                  {t.loadDetail.actions.printInvoice}
                </div>
              ),
              onAction: handlePrintInvoice,
              disabled: isPrinting,
            },
          ]
        : []),
      ...(!isFinished(load.status)
        ? [
            {
              label: (
                <div className="flex items-center gap-2">
                  <FileCode2 className="size-4" />
                  {load.salesOrderId
                    ? t.loadDetail.actions.reassignSO
                    : t.loadDetail.actions.assignSO}
                </div>
              ),
              onAction: handleReassignSO,
            },
            {
              label: (
                <div className="flex items-center gap-2">
                  <FileCode2 className="size-4" />
                  {load.bookingId
                    ? t.loadDetail.actions.reassignBooking
                    : t.loadDetail.actions.assignBooking}
                </div>
              ),
              onAction: handleReassignBooking,
            },
          ]
        : []),
      ...(revertStatus && revertStatusLabel
        ? [
            {
              label: (
                <div className="flex items-center gap-2">
                  <Undo2 className="size-4" />
                  {revertStatusLabel}
                </div>
              ),
              onAction: handleRevertStatus,
            },
          ]
        : []),
      ...(load.status !== enumLoadStatus.Voided &&
      load.status !== enumLoadStatus.Reconciled &&
      onVoid
        ? [
            {
              label: (
                <div className="flex items-center gap-2 text-failure">
                  <Trash2 className="size-4" />
                  {t.loadDetail.actions.voidLoad}
                </div>
              ),
              onAction: handleVoidLoad,
            },
          ]
        : []),
    ],
    [
      load.salesOrderId,
      load.bookingId,
      load.status,
      handlePrintPackingList,
      handlePrintBillOfLading,
      handleReassignSO,
      handleReassignBooking,
      handleRevertStatus,
      handlePrintInvoice,
      handleVoidLoad,
      load.invoiceId,
      revertStatus,
      revertStatusLabel,
      t,
      isPrinting,
      onVoid,
    ],
  );

  const popovers = React.useMemo(
    () => (
      <>
        <LoadAssignSalesOrderPopover
          isOpen={isSalesOrderPopoverOpen}
          onOpenChange={setIsSalesOrderPopoverOpen}
          loadId={load.id}
          isUnassigned={load.status === enumLoadStatus.Unassigned}
          currentSalesOrderId={load.salesOrderId}
          heavyWeight={load.heavyWeight}
          lightWeight={load.lightWeight}
        />
        <LoadAssignBookingPopover
          isOpen={isBookingPopoverOpen}
          onOpenChange={setIsBookingPopoverOpen}
          loadId={load.id}
          currentBookingId={load.bookingId}
          heavyWeight={load.heavyWeight}
          lightWeight={load.lightWeight}
        />
      </>
    ),
    [
      isSalesOrderPopoverOpen,
      load.id,
      load.status,
      load.salesOrderId,
      load.heavyWeight,
      load.lightWeight,
      load.bookingId,
      isBookingPopoverOpen,
    ],
  );

  return { menuItems, popovers };
};
